import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import Config, { EnvConfig } from 'config/config';
import posthog from 'posthog-js';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { hasPendingConnectToken } from '../../connect-token';
import {
  buildVendorPortalDestinationUrl,
  isSafeVendorsBaseUrl
} from '../../vendor-redirect';
import { AuthService } from '../services/auth.service';
import { PortalRoutingService } from '../services/portal-routing.service';

/** PostHog feature flag gating the legacy → vive-vendors redirect rollout. */
const VENDOR_APP_REDIRECT_FLAG = 'vendor-app-redirect';

/**
 * Force a cut-over vendor out of the legacy app on EVERY route.
 *
 * The pre-bootstrap redirect (`main.ts`) only fires once at page load, so a
 * vendor who should be on the new portal can still end up inside the legacy SPA
 * (the routing fetch timed out, the per-tab handoff guard was already set, or
 * they signed in through the in-app form, which is an Angular navigation, not a
 * fresh page load) and then navigate freely. This guard re-applies the full
 * redirect decision on every in-app navigation and sends the vendor to
 * vive-vendors, which reads the same session cookie.
 *
 * The decision matches `main.ts` exactly: the sticky `useVendorPortal` flag OR
 * the `vendor-app-redirect` PostHog rollout (flag enabled + the vendor's
 * CompanyId in the payload).
 *
 * Non-vendor, signed-out, impersonation and not-cut-over sessions pass straight
 * through, so it is safe to attach to any vendor-reachable route tree.
 */
@Injectable({
  providedIn: 'root'
})
export class VendorPortalRedirectGuard {
  // Taken via the DOCUMENT token rather than the global so the hand-off is
  // spy-able: `window.location.replace` is non-writable in current Chrome.
  // `document.location` IS `window.location` at runtime.
  private readonly document = inject(DOCUMENT);

  public constructor(
    private portalRoutingService: PortalRoutingService,
    private authService: AuthService
  ) {}

  public canActivate(): Observable<boolean> {
    return this.redirectToVendorPortalIfForced();
  }

  /**
   * The shared redirect decision, extracted from `canActivate` so callers
   * outside the router (e.g. the re-login modal, which re-authenticates via an
   * in-app HTTP flow rather than a route change) can re-evaluate the cut-over on
   * demand. Emits `true` when it is safe to stay in the legacy app and `false`
   * once it has initiated `location.replace(...)` to vive-vendors — on that
   * branch the page is already unloading, so callers must not treat the
   * emission as "continue in the legacy app".
   */
  public redirectToVendorPortalIfForced(): Observable<boolean> {
    const { enableVendorAppRedirect, vendorsAppUrl, postHog } =
      Config.getEnvironmentConfig();

    // Master kill switch — one flip disables ALL redirects, including this one.
    // Also bail if the configured target isn't a safe origin, so a config
    // regression can never send the user to an arbitrary host. A pending
    // Connect exchange must reach the login page, which replaces this session.
    if (
      !enableVendorAppRedirect ||
      !vendorsAppUrl ||
      !isSafeVendorsBaseUrl(vendorsAppUrl) ||
      hasPendingConnectToken()
    ) {
      return of(true);
    }

    const vendorId = this.authService.getRedirectableVendorId();
    if (vendorId === null) {
      return of(true);
    }

    const { flagEnabled, payload } = this.resolveRedirectFlag(postHog);

    return this.portalRoutingService
      .shouldForceVendorPortal(vendorId, flagEnabled, payload)
      .pipe(
        map((shouldRedirect) => {
          if (!shouldRedirect) {
            return true;
          }
          // Deliberately ignores the pre-bootstrap HANDOFF_GUARD_KEY: honouring
          // it here would re-open the very gap this guard closes. The cross-app
          // loop stays bounded by vive-vendors' own redirect guard.
          this.document.location.replace(
            buildVendorPortalDestinationUrl(vendorsAppUrl, '/')
          );
          return false;
        })
      );
  }

  /**
   * Read the `vendor-app-redirect` rollout inputs, mirroring `main.ts`:
   *  - with PostHog configured (deployed envs) → the live flag + payload;
   *  - without it (local dev) → the `vendor-app-redirect` localStorage override
   *    so the full flow stays testable without a live PostHog connection.
   */
  private resolveRedirectFlag(postHog: EnvConfig['postHog']): {
    flagEnabled: boolean;
    payload: string[] | null;
  } {
    if (!postHog) {
      return {
        flagEnabled: localStorage.getItem(VENDOR_APP_REDIRECT_FLAG) === 'true',
        payload: null
      };
    }
    return {
      flagEnabled: posthog.isFeatureEnabled(VENDOR_APP_REDIRECT_FLAG) === true,
      payload:
        (posthog.getFeatureFlagPayload(VENDOR_APP_REDIRECT_FLAG) as
          string[] | null) ?? null
    };
  }
}
